// Typed error hierarchy + a single handleError() entry point. Lets the API layer
// throw errors that preserve HTTP status/code/details, and lets components collapse
// the "console.error + setError + alert/toast" boilerplate into one call.
// See docs/error-handling.md.

import { createLogger, logger as rootLogger } from "./logger";
import { notify } from "./notify";

// Base for every error we throw deliberately. Carries a stable, machine-readable
// `code` and preserves the original `cause` so logs keep the full story.
export class AppError extends Error {
  code: string;
  cause?: unknown;

  constructor(message: string, code = "APP_ERROR", cause?: unknown) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.cause = cause;
    // Restore the prototype chain so `instanceof` works after TS downlevels to ES5.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// An HTTP request failed. `status` is the HTTP status (0 when no response was
// received, i.e. a network failure); `details` holds the backend payload
// (validation errors, etc.).
export class ApiError extends AppError {
  status: number;
  details?: unknown;

  constructor(
    message: string,
    status: number,
    code = "API_ERROR",
    details?: unknown,
    cause?: unknown
  ) {
    super(message, code, cause);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

// No response reached us (offline, DNS, CORS, timeout). Always status 0.
export class NetworkError extends ApiError {
  constructor(
    message = "Network error - please check your connection",
    cause?: unknown
  ) {
    super(message, 0, "NETWORK", undefined, cause);
    this.name = "NetworkError";
  }
}

// A client-side validation failure (not from the server).
export class ValidationError extends AppError {
  constructor(message: string, cause?: unknown) {
    super(message, "VALIDATION", cause);
    this.name = "ValidationError";
  }
}

/**
 * Map any thrown value to a friendly, non-leaky user message. Centralizes the
 * HTTP-status -> copy table so every screen says the same thing for the same
 * failure. (Replaces scattered `error instanceof Error ? …` ternaries.)
 */
export function getUserMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return "Your session has expired. Please sign in again.";
      case 403:
        return "You don't have access to do that.";
      case 404:
        return "We couldn't find what you were looking for.";
      case 429:
        return "Too many requests. Please wait a moment and try again.";
      default:
        if (error.status >= 500)
          return "Something went wrong on our end. Please try again.";
        return error.message || "Request failed. Please try again.";
    }
  }
  if (error instanceof AppError) return error.message;
  if (error instanceof Error)
    return error.message || "An unexpected error occurred.";
  return "An unexpected error occurred.";
}

export interface HandleErrorOptions {
  /** Toast the user: true (friendly message) | a custom string | false. Default true. */
  notify?: boolean | string;
  /** Re-throw after handling, for callers that still need the rejection. Default false. */
  rethrow?: boolean;
  /** Logger scope, e.g. "GitAccounts". */
  scope?: string;
  /** Extra structured data attached to the log record. */
  context?: Record<string, unknown>;
}

/**
 * The single place an error should be funneled to. It (1) logs the technical
 * detail + context via the logger, and (2) by default shows the user a friendly
 * toast. Returns the friendly message so callers can also render it inline.
 */
export function handleError(
  error: unknown,
  options: HandleErrorOptions = {}
): string {
  const {
    notify: shouldNotify = true,
    rethrow = false,
    scope,
    context,
  } = options;
  const log = scope ? createLogger(scope) : rootLogger;
  const userMessage = getUserMessage(error);

  // Always log the full error + context; the user only ever sees `userMessage`.
  log.error(userMessage, { error, ...context });

  if (shouldNotify) {
    notify.error(typeof shouldNotify === "string" ? shouldNotify : userMessage);
  }
  if (rethrow) throw error;
  return userMessage;
}
